import {Module} from "vuex";
import {AsyncJavaRequest, Main} from "@/controller/main";
import {IDBase, QueryFacade} from "@/controller/QueryFacade";

const availableCurrencies = [
    "USD",
    "EUR",
    "JPY",
    "GBP",
    "AUD",
    "CAD",
    "CHF",
    "CNY",
    "HKD",
    "NZD",
    "SEK",
    "KRW",
    "SGD",
    "NOK",
    "MXN",
    "INR",
    "RUB",
    "ZAR",
    "TRY",
    "BRL",
    "RON"
];

export class Rate {
    sourceCurrency: string = "";
    targetCurrency: string = "";
    exchangeRate: string = "1";

    constructor(sourceCurrency: string, targetCurrency: string, exchangeRate: string) {
        this.sourceCurrency = sourceCurrency;
        this.targetCurrency = targetCurrency;
        this.exchangeRate = exchangeRate;
    }
}

export class ExchangeRateSnapshot {
    public snapshotTime: number = Date.now();
    public snapshotComment: string = "";
    public frozen: boolean = true;
    public rates: Rate[] = [];
}

export class Program implements IDBase {
    id: string = "0";
    label: string = "";
    public exchangeRateSnapshots: ExchangeRateSnapshot[] = [];

    constructor(id: string, label: string) {
        this.id = id;
        this.label = label;
    }
}

export class CurrencyState {

    public selectedExchangeRateSnapshot: ExchangeRateSnapshot | null = null;
    public programs: Program[] = [];
    public selectedProgram: Program | null = null;
    public canCreateSnapshot = false;
}

export class HasRightResponse<T extends {}> {
    public rights?: T;
}

export class HasRightsRequest {
    public rightTags: string[] = [];
}

class CurrencyStore implements Module<any, unknown> {
    public namespaced = true;
    public state: CurrencyState = new CurrencyState();

    public getters = {
        exchangeRateSnapshots(state: CurrencyState) {
            if (!state.selectedProgram) {
                return;
            }

            return state.selectedProgram.exchangeRateSnapshots;
        },
        selectedExchangeRateSnapshot(state: CurrencyState) {
            return state.selectedExchangeRateSnapshot;
        },

        programs(state: CurrencyState) {
            return state.programs;
        },

        selectedProgram(state: CurrencyState) {
            return state.selectedProgram;
        },
        canCreateSnapshot(state: CurrencyState) {
            return state.canCreateSnapshot;
        },
        selectableCurrencies(state:CurrencyState){
            return availableCurrencies;
        }
    };

    public mutations = {
        updateMessage(state: any, message: string) {
            state.toSay = message
        },
        setActiveSnapshot(state: any, message: string) {
            state.selectedExchangeRateSnapshot = message
        },
        addSnapshot(state: CurrencyState, message: ExchangeRateSnapshot) {
            if (!state.selectedProgram) {
                return;
            }

            for (let rate of message.rates) {
                const exRate = rate.exchangeRate.replace(/,/g, ".");
                rate.exchangeRate = Number.parseFloat(exRate).toFixed(8);
            }
            state.selectedProgram.exchangeRateSnapshots.push(message);
            CurrencyStore.sortSnapshots(state);
            QueryFacade.put("currency_states", state.selectedProgram);
        },
        setActiveProgram(state: CurrencyState, message: Program) {
            state.selectedProgram = message;
            CurrencyStore.sortSnapshots(state);
            state.selectedExchangeRateSnapshot = state.selectedProgram.exchangeRateSnapshots[0];
        },

        async loadFromDb(state: any) {
            const data = await Main.postMessage(new AsyncJavaRequest("call", "allPrograms"));
            currencyStore.state.programs = [];
            const programs: { id: string, name: string }[] = (<any>data)["programs"];
            programs.sort((a, b) => {
                return ('' + a.name).localeCompare(b.name);
            });
            for (let program of programs) {
                const p = new Program(program.id, program.name);
                currencyStore.state.programs.push(p);

                try {
                    const exchangeRateSnapshots = await loadExchangeRates(program.id);
                    if (exchangeRateSnapshots.length > 0) {
                        p.exchangeRateSnapshots = exchangeRateSnapshots;
                    }
                } catch (e) {
                    console.error("during exchange rate fetching", e);
                }
            }

            const createSnapReadTag = "admin_config_currencies_edit";
            const request = new HasRightsRequest();
            request.rightTags = [createSnapReadTag];
            const rightResponse: HasRightResponse<{ admin_config_currencies_edit: boolean }> = await Main.postMessage(
                new AsyncJavaRequest("call", "hasRights", JSON.stringify(request))
            );

            currencyStore.state.canCreateSnapshot = (
                rightResponse.rights ? rightResponse.rights : {
                    admin_config_currencies_edit: false
                }).admin_config_currencies_edit;
        }
    };

    private static sortSnapshots(state: CurrencyState) {
        if (state.selectedProgram !== null) {
            state.selectedProgram.exchangeRateSnapshots.sort(((a, b) => {
                return b.snapshotTime - a.snapshotTime;
            }));
        }
    }
}

export const currencyStore = new CurrencyStore();

export async function loadExchangeRates(id: string): Promise<ExchangeRateSnapshot[]> {
    const res = await <Program><any>QueryFacade.get("currency_states", id);
    if (res != null) {
        return res.exchangeRateSnapshots.sort(((a, b) => {
            return b.snapshotTime - a.snapshotTime;
        }));
    }
    return [];

}


